import React from 'react';
import { useTranslation } from 'react-i18next';
import { VatBreakdownItem } from '../types';
import { formatCurrency, formatVatRate, getVatExemptionMention } from '../../../utils/vatCalculations';

interface TotalsSectionProps {
  subtotal: number;
  tax: number;
  total: number;
  discount?: number;
  discountType?: string;
  discountValue?: number;
  vatBreakdown?: VatBreakdownItem[];
  accentColor?: string;
  showVatBreakdown?: boolean;

  // Conformité EN 16931 (Factur-X)
  displayMode?: 'simple' | 'detailed';
  isVatExempt?: boolean;
  vatExemptionMention?: string;
  vatRegime?: string;
}

/**
 * Section d'affichage des totaux avec ventilation TVA
 * Conforme EN 16931 en mode 'detailed'
 */
export const TotalsSection: React.FC<TotalsSectionProps> = ({
  subtotal,
  tax,
  total,
  discount = 0,
  discountType,
  discountValue,
  vatBreakdown = [],
  accentColor = '#0D9488',
  showVatBreakdown = true,
  displayMode = 'simple',
  isVatExempt = false,
  vatExemptionMention,
  vatRegime
}) => {
  const { t } = useTranslation();
  // Mode simple (ancien comportement, conservé pour compatibilité)
  if (displayMode === 'simple') {
    return (
      <div className="flex flex-col items-end">
        <div className="w-full md:w-1/2 space-y-3">
          {/* Sous-total */}
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="text-gray-600 font-medium">{t('templates.common.subtotalHT')}</span>
            <span className="text-gray-800 font-semibold">{formatCurrency(subtotal)}</span>
          </div>

          {/* Remise */}
          {discount > 0 && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600 font-medium">
                {t('templates.common.discount')}
                {discountType === 'percentage' && discountValue
                  ? ` (${discountValue}%)`
                  : ''}
              </span>
              <span className="text-red-600 font-semibold">- {formatCurrency(discount)}</span>
            </div>
          )}

          {/* Ventilation TVA - uniquement si plusieurs taux différents */}
          {showVatBreakdown && vatBreakdown.length > 1 && !isVatExempt && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">{t('templates.common.vatBreakdown')}</h4>
              {vatBreakdown.map((vat, index) => (
                <div key={index} className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">
                    {t('templates.common.vat')} {formatVatRate(vat.rate)} {t('templates.common.on')} {formatCurrency(vat.baseHT)}
                  </span>
                  <span className="text-gray-800 font-medium">{formatCurrency(vat.vatAmount)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 border-t border-gray-300">
                <span className="text-gray-600 font-medium">{t('templates.common.totalVAT')}</span>
                <span className="text-gray-800 font-semibold">{formatCurrency(tax)}</span>
              </div>
            </div>
          )}

          {/* TVA simple si un seul taux ou pas de ventilation */}
          {(vatBreakdown.length <= 1) && tax > 0 && !isVatExempt && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-gray-600 font-medium">
                {t('templates.common.vat')} {vatBreakdown.length === 1 ? formatVatRate(vatBreakdown[0].rate) : ''}
              </span>
              <span className="text-gray-800 font-semibold">{formatCurrency(tax)}</span>
            </div>
          )}

          {/* Mention exemption TVA */}
          {isVatExempt && (
            <div className="py-2 px-3 bg-gray-50 rounded text-xs italic text-gray-600">
              {vatExemptionMention || getVatExemptionMention(vatRegime)}
            </div>
          )}

          {/* Total TTC */}
          <div
            className="flex justify-between items-center py-3 px-4 rounded-lg"
            style={{ backgroundColor: `${accentColor}15` }}
          >
            <span className="text-lg font-bold text-gray-800">{t('templates.common.totalTTC')}</span>
            <span
              className="text-xl md:text-2xl font-bold"
              style={{ color: accentColor }}
            >
              {formatCurrency(total)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Mode detailed (conforme EN 16931 pour Factur-X)
  // Calculer les totaux à partir de vatBreakdown pour cohérence
  const calculatedSubtotal = vatBreakdown.reduce((sum, vat) => sum + vat.baseHT, 0);
  const calculatedTax = vatBreakdown.reduce((sum, vat) => sum + vat.vatAmount, 0);
  const calculatedTotal = vatBreakdown.reduce((sum, vat) => sum + (vat.totalTTC ?? 0), 0);

  // Sous-total avant remise (pour affichage)
  const subtotalBeforeDiscount = discount > 0 ? subtotal + discount : subtotal;

  return (
    <div className="flex justify-end mb-8">
      <div className="w-full md:max-w-xl">
        <div className="bg-gray-50 border border-gray-300 rounded-lg overflow-hidden">
          {/* Sous-total HT avant remise */}
          <div className="px-3 md:px-6 py-2 md:py-3 border-b border-gray-300 flex justify-between text-sm md:text-base">
            <span className="font-medium">{t('templates.common.subtotalHT')} :</span>
            <span className="font-semibold">{formatCurrency(subtotalBeforeDiscount)}</span>
          </div>

          {/* Remise si appliquée */}
          {discount > 0 && (
            <div className="px-3 md:px-6 py-2 md:py-3 border-b border-gray-300 flex justify-between bg-green-50 text-sm md:text-base">
              <span className="font-medium text-green-700">
                {t('templates.common.discount')}{' '}
                {discountType === 'percentage' && discountValue ? `(${discountValue}%)` : ''} :
              </span>
              <span className="font-semibold text-green-700">- {formatCurrency(discount)}</span>
            </div>
          )}

          {/* Mention exemption TVA */}
          {isVatExempt ? (
            <div className="px-3 md:px-4 py-2 border-b border-gray-300">
              <p className="text-xs italic text-gray-600">
                {vatExemptionMention || getVatExemptionMention(vatRegime)}
              </p>
            </div>
          ) : vatBreakdown.length > 1 ? (
            /* Table détaillée TVA (conforme EN 16931) - uniquement si plusieurs taux */
            <div className="px-2 md:px-4 py-2 border-b border-gray-300">
              <h4 className="font-semibold text-xs mb-1">{t('templates.common.vatDetail')} :</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left py-0.5 px-0.5 md:px-1 text-xs">{t('templates.common.rate')}</th>
                      <th className="text-right py-0.5 px-0.5 md:px-1 text-xs">{t('templates.common.baseHT')}</th>
                      <th className="text-right py-0.5 px-0.5 md:px-1 text-xs">{t('templates.common.vat')}</th>
                      <th className="text-right py-0.5 px-0.5 md:px-1 text-xs">{t('templates.common.ttc')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vatBreakdown.map((vat) => (
                      <tr key={vat.rate} className="border-t border-gray-200">
                        <td className="py-0.5 px-0.5 md:px-1 text-xs">{formatVatRate(vat.rate)}</td>
                        <td className="text-right py-0.5 px-0.5 md:px-1 text-xs">{formatCurrency(vat.baseHT)}</td>
                        <td className="text-right py-0.5 px-0.5 md:px-1 text-xs">{formatCurrency(vat.vatAmount)}</td>
                        <td className="text-right py-0.5 px-0.5 md:px-1 text-xs">{formatCurrency(vat.totalTTC ?? 0)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-gray-400 font-semibold">
                      <td className="py-0.5 px-0.5 md:px-1 text-xs">TOTAL</td>
                      <td className="text-right py-0.5 px-0.5 md:px-1 text-xs">
                        {formatCurrency(calculatedSubtotal)}
                      </td>
                      <td className="text-right py-0.5 px-0.5 md:px-1 text-xs">
                        {formatCurrency(calculatedTax)}
                      </td>
                      <td className="text-right py-0.5 px-0.5 md:px-1 text-xs">
                        {formatCurrency(calculatedTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : vatBreakdown.length === 1 && calculatedTax > 0 ? (
            /* TVA simple si un seul taux */
            <div className="px-3 md:px-6 py-2 md:py-3 border-b border-gray-300 flex justify-between text-sm md:text-base">
              <span className="font-medium">{t('templates.common.vat')} {formatVatRate(vatBreakdown[0].rate)} :</span>
              <span className="font-semibold">{formatCurrency(calculatedTax)}</span>
            </div>
          ) : null}

          {/* Total TTC */}
          <div
            className="px-3 md:px-6 py-3 md:py-4 text-white flex justify-between"
            style={{ backgroundColor: accentColor }}
          >
            <span className="font-bold text-base md:text-lg">{t('templates.common.totalTTC')} :</span>
            <span className="font-bold text-lg md:text-xl">
              {formatCurrency(vatBreakdown.length > 0 ? calculatedTotal : total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
